import { Alert, Button, Card, DatePicker, Input, message, Modal, Space, Spin, Table, Typography } from "antd";
import moment from "moment";
import { useUploadFileToS3Mutation } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useState } from "react";
import excelService, { ExcelExtractionResult } from "../../services/excel/excelService";
import logger from "../../services/logger/logger";
import extractExcelBase64FromJson from "../../utils/excelUtils";
import "./ExcelDataExtractor.scss";

const { Title, Text } = Typography;

interface ExcelDataExtractorProps {
  jsonData: any;
  onConfirm: (extractedData: any) => void;
  onCancel: () => void;
  onError?: (errorMessage: string) => void;
}

interface ExtractedData extends ExcelExtractionResult {
  consumptionFileURL?: string;
  s3Upload?: S3UploadResult | null;
}

interface S3UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

function excelDateToJSDate(serial: number): Date {
  // Excel's epoch starts at 1900-01-01
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400; // seconds
  const dateInfo = new Date(utcValue * 1000);
  // Adjust for Excel's leap year bug
  if (serial < 60) {
    dateInfo.setDate(dateInfo.getDate() - 1);
  }
  return dateInfo;
}

const validateExcelFormat = (columns: string[]): { isValid: boolean; error?: string } => {
  const requiredColumns = [
    { name: "Type of Data", alternatives: ["data type", "type of data", "data_type"] },
    { name: "Month, Year", alternatives: ["month, year", "date", "month_year"] },
    { name: "Electricity Consumption (kWh)", alternatives: ["electricity consumption", "electricity_consumption"] },
    {
      name: "Electricity Consumption Cost (Local Currency)",
      alternatives: ["electricity consumption cost", "electricity_consumption_cost"],
    },
    {
      name: "Average Monthly Occupancy (%)",
      alternatives: ["average monthly occupancy", "occupancy", "occupancy_rate"],
    },
    {
      name: "Sub-Metered/Chiller Reading (kWh)",
      alternatives: ["sub-metered/chiller reading", "sub_metered_chiller_reading"],
    },
    { name: "Water Consumption (M3)", alternatives: ["water consumption", "water_consumption"] },
    {
      name: "Water Consumption Cost (Local Currency)",
      alternatives: ["water consumption cost", "water_consumption_cost"],
    },
    { name: "Gas Consumption (M3)", alternatives: ["gas consumption", "gas_consumption"] },
    { name: "Gas Consumption Cost (Local Currency)", alternatives: ["gas consumption cost", "gas_consumption_cost"] },
    { name: "Waste Consumption (MT)", alternatives: ["waste consumption", "waste_consumption"] },
    {
      name: "Waste Consumption Cost (Local Currency)",
      alternatives: ["waste consumption cost", "waste_consumption_cost"],
    },
  ];

  const normalizedColumns = columns.map((col) => col.toLowerCase());

  const missingColumns = requiredColumns.filter((required) => {
    const hasColumn = required.alternatives.some((alt) =>
      normalizedColumns.some((col) => col.includes(alt.toLowerCase()))
    );
    return !hasColumn;
  });

  if (missingColumns.length > 0) {
    return {
      isValid: false,
      error: `Missing required columns: ${missingColumns.map((col) => col.name).join(", ")}`,
    };
  }

  return { isValid: true };
};

const validateNumericalValues = (columns: string[], rows: any[][]): { isValid: boolean; error?: string } => {
  const nonNumericalColumns = ["data type", "type of data", "month, year", "month", "year", "date"];

  const numericalColumnIndices = columns
    .map((col, index) => ({ col: col.toLowerCase(), index }))
    .filter(({ col }) => !nonNumericalColumns.some((nonNum) => col.includes(nonNum.toLowerCase())))
    .map(({ index }) => index);

  // Updated regex to check if the value contains any non-numeric characters
  const containsNonNumericRegex = /[^\d.\s,%$-]/;

  // Regex for valid numbers (accepts negatives, decimals)
  const validNumberRegex = /^-?\d*\.?\d+$/;

  const validateCell = (value: any, colIndex: number, rowIndex: number): { isValid: boolean; error?: string } => {
    const cellValue = value?.toString().trim();
    const columnName = columns[colIndex];

    // Skip empty cells
    if (!cellValue) return { isValid: true };

    // Special case for percentage values
    if (columnName.toLowerCase().includes("occupancy") || columnName.toLowerCase().includes("percentage")) {
      // Allow % character but check if it contains other non-numeric chars
      const valueWithoutPercent = cellValue.replace(/%/g, "").trim();

      if (containsNonNumericRegex.test(valueWithoutPercent)) {
        return {
          isValid: false,
          error: `Invalid percentage value found in row ${
            rowIndex + 1
          }, column "${columnName}": "${cellValue}". Please enter a valid percentage (e.g., 75 or 75%).`,
        };
      }

      // Check if the remaining value is a valid number
      if (!validNumberRegex.test(valueWithoutPercent)) {
        return {
          isValid: false,
          error: `Invalid percentage value found in row ${
            rowIndex + 1
          }, column "${columnName}": "${cellValue}". Please enter a valid percentage (e.g., 75 or 75%).`,
        };
      }

      return { isValid: true };
    }

    // Special case for cost values
    if (columnName.toLowerCase().includes("cost")) {
      // Allow $ and , characters but check if it contains other non-numeric chars
      const valueWithoutCurrency = cellValue.replace(/[$,]/g, "").trim();

      if (containsNonNumericRegex.test(valueWithoutCurrency)) {
        return {
          isValid: false,
          error: `Invalid cost value found in row ${
            rowIndex + 1
          }, column "${columnName}": "${cellValue}". Please enter a valid number (e.g., 500 or $500).`,
        };
      }

      // Check if the remaining value is a valid number
      if (!validNumberRegex.test(valueWithoutCurrency)) {
        return {
          isValid: false,
          error: `Invalid cost value found in row ${
            rowIndex + 1
          }, column "${columnName}": "${cellValue}". Please enter a valid number (e.g., 500 or $500).`,
        };
      }

      return { isValid: true };
    }

    // For consumption and other numerical values, first check for non-numeric characters
    if (containsNonNumericRegex.test(cellValue)) {
      return {
        isValid: false,
        error: `Invalid value found in row ${
          rowIndex + 1
        }, column "${columnName}": "${cellValue}". Only numbers are allowed (values like "100m³" or "1000abc" are not acceptable).`,
      };
    }

    // Then clean allowed characters (commas) and check if it's a valid number
    const cleanValue = cellValue.replace(/[,]/g, "").trim();
    if (!validNumberRegex.test(cleanValue)) {
      return {
        isValid: false,
        error: `Invalid numerical value found in row ${
          rowIndex + 1
        }, column "${columnName}": "${cellValue}". Please ensure the value is a valid number.`,
      };
    }

    return { isValid: true };
  };

  const validationResult = rows.reduce(
    (acc, row, rowIndex) => {
      if (!acc.isValid) return acc;

      const rowValidation = numericalColumnIndices.reduce(
        (rowAcc, colIndex) => {
          if (!rowAcc.isValid) return rowAcc;
          return validateCell(row[colIndex], colIndex, rowIndex);
        },
        { isValid: true }
      );

      return rowValidation;
    },
    { isValid: true }
  );

  return validationResult;
};

// Add this new validation function after the other validation functions
const validateWorksheet = (workbook: any, columns: string[], rows: any[][]): { isValid: boolean; error?: string } => {
  // Check for multiple sheets
  if (workbook.SheetNames.length > 1) {
    return {
      isValid: false,
      error: "Multiple worksheets detected. Please upload a file with only one worksheet containing your data.",
    };
  }

  // Check if we have any data
  if (!rows || rows.length === 0) {
    return {
      isValid: false,
      error: "No data found in the worksheet. Please ensure your file contains data.",
    };
  }

  // Check if we have headers
  if (!columns || columns.length === 0) {
    return {
      isValid: false,
      error: "No headers found in the worksheet. Please ensure your file includes column headers.",
    };
  }

  // Check for minimum required columns
  const requiredColumnCount = 12; // Based on the template format
  if (columns.length < requiredColumnCount) {
    return {
      isValid: false,
      error: `Invalid worksheet format. Expected at least ${requiredColumnCount} columns but found ${columns.length}. Please use the correct template.`,
    };
  }

  return { isValid: true };
};

const ExcelDataExtractor: React.FC<ExcelDataExtractorProps> = ({ jsonData, onConfirm, onCancel, onError }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [editDate, setEditDate] = useState<moment.Moment | null>(null);
  const [s3UploadResult, setS3UploadResult] = useState<S3UploadResult | null>(null);
  const [uploadFileToS3Mutation] = useUploadFileToS3Mutation();

  // Upload the file to S3
  const uploadFileToS3 = async (excelData: { base64Content: string; fileName: string; fileType: string }) => {
    if (!excelData || !excelData.base64Content) {
      logger.error("No valid file data to upload to S3");
      setS3UploadResult({
        success: false,
        error: "No valid file data to upload",
      });
      return;
    }

    try {
      // Call the GraphQL mutation
      const { data } = await uploadFileToS3Mutation({
        variables: {
          input: {
            file: excelData.base64Content,
            fileName: excelData.fileName,
            fileType: excelData.fileType,
          },
        },
      });
      const url = data?.uploadFileToS3;
      const key = url ? url.split("/").slice(-2).join("/") : undefined;
      setS3UploadResult({
        success: !!url,
        url,
        key,
      });
      message.success("File uploaded to S3 successfully");
    } catch (err) {
      logger.error("Error uploading file to S3:", err);
      setS3UploadResult({
        success: false,
        error: err instanceof Error ? err.message : "Unknown error uploading to S3",
      });
      message.error("Failed to upload file to S3");
    }
  };

  const extractData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Extract the base64 content from the JSON data
      const excelData = extractExcelBase64FromJson(jsonData);

      if (!excelData) {
        const errorMsg = "No Excel file data found in the JSON";
        setError(errorMsg);
        message.error("No Excel file data found. Please upload a valid Excel file.");

        // Call onError callback if provided
        if (onError) {
          onError("No Excel file data found. Please upload a valid Excel file.");
        }

        setLoading(false);
        return;
      }

      // Extract data from the Excel file
      const extractedResult = await excelService.extractDataFromExcel(excelData);

      // Validate worksheet structure
      const worksheetValidation = validateWorksheet(
        extractedResult.workbook,
        extractedResult.columns,
        extractedResult.rows
      );

      if (!worksheetValidation.isValid) {
        const validationError = worksheetValidation.error || "Invalid worksheet format";
        setError(validationError);
        message.error(validationError);

        // Call onError callback if provided
        if (onError) {
          onError(validationError);
        }

        setLoading(false);
        return;
      }

      // Validate the Excel format (column names)
      const formatValidation = validateExcelFormat(extractedResult.columns);
      if (!formatValidation.isValid) {
        const validationError = formatValidation.error || "Unknown validation error";
        setError(validationError);
        message.error(`Invalid Excel format. ${validationError} Please use the correct template and try again.`);

        // Call onError callback if provided
        if (onError) {
          onError(`Invalid Excel format. ${validationError} Please use the correct template and try again.`);
        }

        setLoading(false);
        return;
      }

      // Validate numerical values
      const numericalValidation = validateNumericalValues(extractedResult.columns, extractedResult.rows);
      if (!numericalValidation.isValid) {
        const validationError = numericalValidation.error || "Unknown validation error";
        setError(validationError);
        message.error(`${validationError} Please correct the values and try again.`);

        // Call onError callback if provided
        if (onError) {
          onError(`${validationError} Please correct the values and try again.`);
        }

        setLoading(false);
        return;
      }

      // Set the extracted data
      setExtractedData(extractedResult);

      // Upload the Excel file to S3
      await uploadFileToS3(excelData);
    } catch (err) {
      logger.error("Error extracting data:", err);
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError(errorMessage);
      message.error(`Failed to process Excel file: ${errorMessage}`);

      // Call onError callback if provided
      if (onError) {
        onError(`Failed to process Excel file: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Extract data automatically when component mounts
  useEffect(() => {
    extractData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonData]);

  // Normalize column names to a standard format
  const normalizeColumnNames = (columns: string[]): string[] => {
    // Define specific mappings for the Excel columns
    const columnMappings: Record<string, string> = {
      // Essential columns
      type_of_data: "data_type",
      month: "month",
      year: "year",
      "month,_year": "date", // Add mapping for combined month,_year column

      // Consumption columns with exact mappings
      "electricity_consumption_(kwh)": "electricity_consumption",
      "electricity_consumption_cost_(local_currency)": "electricity_consumption_cost",
      "average_monthly_occupancy_(%)": "occupancy",
      average_monthly_occupancy: "occupancy",
      "sub-metered/chiller_reading_(kwh)": "sub_metered_chiller_reading",
      "sub_metered_chiller_reading_(kwh)": "sub_metered_chiller_reading",
      "sub_metered_rooms_(kwh)": "sub_metered_chiller_reading",
      "water_consumption_(m3)": "water_consumption",
      "water_consumption_cost_(local_currency)": "water_consumption_cost",
      "gas_consumption_(m3)": "gas_consumption",
      "gas_consumption_cost_(local_currency)": "gas_consumption_cost",
      "waste_consumption_(mt)": "waste_consumption",
      "waste_consumption_cost_(local_currency)": "waste_consumption_cost",
    };

    // Normalize each column name
    return columns.map((column) => {
      // Convert to lowercase and replace spaces with underscores
      const normalizedName = column.toLowerCase().replace(/\s+/g, "_").replace(/[/-]/g, "_");

      // Check if this column has a mapping
      if (columnMappings[normalizedName]) {
        return columnMappings[normalizedName];
      }

      // Special case for data_type variations
      if (
        normalizedName.includes("type_of_data") ||
        (normalizedName.includes("actual") && normalizedName.includes("forecast"))
      ) {
        return "data_type";
      }

      // No mapping found, return the normalized name
      return normalizedName;
    });
  };

  // Transform Excel data from columns and rows into an array of objects
  const transformExcelDataToObjects = (data: ExtractedData): any[] => {
    if (!data.columns || !data.rows || data.columns.length === 0 || data.rows.length === 0) {
      return [];
    }

    // Normalize column names
    const normalizedColumns = normalizeColumnNames(data.columns);

    // Convert each row into an object using the normalized column names as keys
    const transformedData = data.rows.map((row) => {
      const obj: Record<string, any> = {};

      // Map each column to its value using normalized column names
      normalizedColumns.forEach((column, index) => {
        let value = row[index] !== undefined ? row[index] : "";

        // For 'average_monthly_occupancy' column, keep both the original name and the normalized 'occupancy' name
        if (
          normalizedColumns[index] === "occupancy" &&
          data.columns[index].toLowerCase().includes("average_monthly_occupancy")
        ) {
          obj.average_monthly_occupancy = value;
        }

        // Format 'Month, Year' column (and equivalents) as date if value is a number (Excel serial)
        if (
          (column === "month, year" || column === "month_year" || column === "month,_year" || column === "date") &&
          typeof value === "number"
        ) {
          const jsDate = excelDateToJSDate(value);
          value = jsDate.toISOString().slice(0, 7); // Format as YYYY-MM
          obj[column] = value;
          return;
        }

        // If value is a string and this is a date column, preserve 'YYYY-MM' format
        if (
          (column === "month, year" || column === "month_year" || column === "month,_year" || column === "date") &&
          typeof value === "string" &&
          /^\d{4}-\d{2}$/.test(value)
        ) {
          obj[column] = value;
          return;
        }

        // If value is a string, try to parse as number if appropriate (but skip for date columns)
        if (
          typeof value === "string" &&
          !(column === "month, year" || column === "month_year" || column === "month,_year" || column === "date")
        ) {
          // Remove any non-numeric characters except decimal point
          const cleanValue = value.replace(/[^0-9.]/g, "");
          const numValue = parseFloat(cleanValue);
          if (!Number.isNaN(numValue)) {
            value = numValue;
          } else {
            // If we can't parse it as a number, set to empty string
            value = "";
          }
        }

        obj[column] = value;
      });

      // --- FIX: Split 'date' field into 'month' and 'year' if present ---
      if (obj.date && typeof obj.date === "string" && /^\d{4}-\d{2}$/.test(obj.date)) {
        const [year, month] = obj.date.split("-");
        obj.year = parseInt(year, 10);
        obj.month = parseInt(month, 10);
      }
      // ---------------------------------------------------------------

      // --- FIX: Ensure 'data_type' is set from the first column if missing or empty ---
      if ((!obj.data_type || obj.data_type === "") && row[0]) {
        obj.data_type = String(row[0]);
      }
      // -------------------------------------------------------------------------------

      return obj;
    });

    return transformedData;
  };

  // Define finalizeConfirmation before handleConfirm to address the "used before defined" error
  const finalizeConfirmation = () => {
    // Since we check for null extractedData in handleConfirm before calling this,
    // we can be sure extractedData is not null here
    if (!extractedData) {
      message.error("Missing extracted data");
      return;
    }

    // Prepare the data for the onConfirm callback
    const dataForCallback = {
      extractedExcelData: transformExcelDataToObjects(extractedData),
      consumptionFileURL: s3UploadResult?.success ? s3UploadResult.url : null,
      originalData: {
        columns: extractedData.columns,
        rows: extractedData.rows,
      },
      s3Upload: s3UploadResult,
    };

    // Pass the data to the parent component
    onConfirm(dataForCallback);
  };

  const handleConfirm = () => {
    if (!extractedData) {
      message.error("No data has been extracted yet");
      return;
    }

    // Check if we have S3 upload result
    if (s3UploadResult && !s3UploadResult.success) {
      // Show a warning that file upload failed but allow continuing
      Modal.confirm({
        title: "Warning: File Upload Failed",
        content: (
          <div>
            <p>
              The Excel file upload to S3 failed. You can still proceed, but the file won&apos;t be stored for later
              use.
            </p>
            <p>Error: {s3UploadResult.error}</p>
            <p>Do you want to proceed anyway?</p>
          </div>
        ),
        onOk: () => {
          // Continue with confirmation despite upload failure
          finalizeConfirmation();
        },
      });
    } else {
      // If upload was successful or no result yet, proceed normally
      finalizeConfirmation();
    }
  };

  // Check if a value is a date string in YYYY-MM-DD format
  const isDateString = (value: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  };

  const startEditing = (rowIndex: number, colIndex: number, value: string) => {
    setEditingCell({ rowIndex, colIndex });
    setEditValue(value);

    // If it's a date, also set the date state for the DatePicker
    if (isDateString(value)) {
      setEditDate(moment(value, "YYYY-MM-DD"));
    } else {
      setEditDate(null);
    }
  };

  const saveEdit = () => {
    if (extractedData && editingCell) {
      const { rowIndex, colIndex } = editingCell;

      // If we're editing a date, use the formatted date value
      let valueToSave = editValue;
      if (editDate) {
        valueToSave = editDate.format("YYYY-MM-DD");
      }

      const updatedData = extractedData.updateCell(rowIndex, colIndex, valueToSave);
      setExtractedData(updatedData);
      setEditingCell(null);
    }
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditDate(null);
  };

  const handleDateChange = (date: any) => {
    // Convert to local moment object
    if (date) {
      const localDate = moment(date.format("YYYY-MM-DD"));
      setEditDate(localDate);
    } else {
      setEditDate(null);
    }
  };

  // Generate table columns from the extracted data
  const generateColumns = () => {
    if (!extractedData || !extractedData.columns) {
      return [];
    }

    return extractedData.columns.map((column, index) => ({
      title: column,
      dataIndex: index.toString(),
      key: index.toString(),
      render: (text: string, record: any, rowIndex: number) => {
        const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.colIndex === index;

        // Check if this cell contains a date
        const isDate = isDateString(text);

        if (isEditing) {
          return (
            <Space>
              {isDate ? (
                <DatePicker
                  value={editDate as any}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                />
              ) : (
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onPressEnter={saveEdit}
                  autoFocus
                />
              )}
              <Button size="small" type="primary" onClick={saveEdit}>
                Save
              </Button>
              <Button size="small" onClick={cancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }

        return (
          <div
            className={`editable-cell ${isDate ? "date-cell" : ""}`}
            onClick={() => startEditing(rowIndex, index, text)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                startEditing(rowIndex, index, text);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Edit ${column} value: ${text}`}
          >
            {text}
          </div>
        );
      },
    }));
  };

  // Generate table data from the extracted data
  const generateTableData = () => {
    if (!extractedData || !extractedData.rows) {
      return [];
    }

    // Use the transformed data
    const transformedRows = transformExcelDataToObjects(extractedData);

    return transformedRows.map((rowObj, rowIndex) => {
      const rowData: Record<string, string> = { key: rowIndex.toString() };
      Object.values(rowObj).forEach((cell, cellIndex) => {
        rowData[cellIndex.toString()] = cell !== undefined && cell !== null ? String(cell) : "";
      });
      return rowData;
    });
  };

  return (
    <div className="excel-data-extractor">
      {loading && (
        <div className="loading-container">
          <Spin size="large" />
          <Text>Extracting data from Excel file...</Text>
        </div>
      )}

      {error && <Alert message="Error" description={error} type="error" showIcon />}

      {extractedData && (
        <div className="extracted-data-container">
          <Card className="extraction-card" title={<Title level={4}>Extracted Data</Title>} style={{ width: "100%" }}>
            {s3UploadResult && s3UploadResult.success && (
              <Alert
                message="File Uploaded Successfully"
                description={
                  <Text>
                    The Excel file has been uploaded to S3.{" "}
                    <a href={s3UploadResult.url} target="_blank" rel="noopener noreferrer">
                      View File
                    </a>
                  </Text>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {s3UploadResult && !s3UploadResult.success && (
              <Alert
                message="File Upload Failed"
                description={s3UploadResult.error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <div className="table-container" style={{ width: "100%" }}>
              <Table
                columns={generateColumns()}
                dataSource={generateTableData()}
                pagination={{ pageSize: 10 }}
                bordered
                size="middle"
                scroll={{ x: "max-content" }}
                style={{ width: "100%" }}
              />
            </div>
          </Card>

          <div className="action-buttons">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleConfirm}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelDataExtractor;
